import {
  S3Client,
  PutObjectCommand,
  DeleteObjectCommand,
} from '@aws-sdk/client-s3';

type UploadMetadata = Record<string, string>;

interface UploadOptions {
  imageName?: string;
  prefix?: string;
  metadata?: UploadMetadata;
}

export interface S3UploadResult {
  imageName: string;
  fullUrl: string;
  key: string;
  originalName: string;
  size: number;
  timestamp: number;
  metadata?: UploadMetadata;
}

const DEFAULT_FOLDER = 'uploads';
const REVIEW_IMAGES_FOLDER = 'review-images';
const PROFILE_IMAGES_FOLDER = 'profile-images';
const FALLBACK_IMAGE_URL = '/fallback-profile-1.avif';

const getOptionalEnv = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const S3_BUCKET = getOptionalEnv(import.meta.env.VITE_S3_BUCKET_NAME);
const REGION = getOptionalEnv(import.meta.env.VITE_S3_BUCKET_REGION);
const ACCESS_KEY = getOptionalEnv(import.meta.env.VITE_S3_ACCESS_KEY);
const SECRET_KEY = getOptionalEnv(import.meta.env.VITE_S3_SECRET_KEY);

// CDN base URL used for rendering public image URLs.
const CDN_BASE_URL = getOptionalEnv(import.meta.env.VITE_APP_CDN_URL) ?? '';

const hasValidS3Config = Boolean(
  S3_BUCKET && REGION && ACCESS_KEY && SECRET_KEY
);

const s3Client = hasValidS3Config
  ? new S3Client({
      region: REGION,
      credentials: {
        accessKeyId: ACCESS_KEY!,
        secretAccessKey: SECRET_KEY!,
      },
    })
  : null;

const ensureClient = () => {
  if (!s3Client) {
    throw new Error(
      'S3 client is not configured. Please verify your AWS environment variables.'
    );
  }
  return s3Client;
};

const trimLeadingSlash = (value: string) => value.replace(/^\/+/, '');
const trimTrailingSlash = (value: string) => value.replace(/\/+$/, '');

const sanitizeFolder = (folderName?: string) => {
  if (!folderName || folderName.trim() === '') {
    return DEFAULT_FOLDER;
  }
  return trimLeadingSlash(trimTrailingSlash(folderName));
};

const stripExtension = (fileName: string) => fileName.replace(/\.[^/.]+$/, '');

const getFileExtension = (fileName: string) => {
  const match = fileName.match(/\.([a-zA-Z0-9]+)$/);
  return match ? match[1].toLowerCase() : 'bin';
};

const slugify = (value: string) => {
  return (
    stripExtension(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80) || 'file'
  );
};

const buildImageName = (
  file: File,
  options: UploadOptions,
  timestamp: number
) => {
  if (options.imageName) {
    return trimLeadingSlash(options.imageName);
  }

  const extension = getFileExtension(file.name);
  const baseName = slugify(file.name);
  const prefix = options.prefix ? `${slugify(options.prefix)}-` : '';
  const randomSuffix = Math.random().toString(36).slice(2, 8);

  return `${prefix}${baseName}-${timestamp}-${randomSuffix}.${extension}`;
};

const buildKey = (imageName: string, folderName: string) => {
  const cleanName = trimLeadingSlash(imageName);
  if (cleanName.startsWith(`${folderName}/`)) {
    return cleanName;
  }
  return `${folderName}/${cleanName}`;
};

const buildPublicUrl = (key: string) => {
  const normalizedKey = trimLeadingSlash(key);
  if (!normalizedKey) {
    return '';
  }

  const base = CDN_BASE_URL.trim();
  if (base) {
    return `${trimTrailingSlash(base)}/${normalizedKey}`;
  }

  return `/${normalizedKey}`;
};

const normalizeMetadata = (metadata?: UploadMetadata) => {
  if (!metadata) {
    return undefined;
  }

  return Object.entries(metadata).reduce<UploadMetadata>(
    (acc, [key, value]) => {
      if (key && typeof value === 'string') {
        acc[key] = value;
      }
      return acc;
    },
    {}
  );
};

export const uploadToS3WithImageName = async (
  file: File,
  folderName = DEFAULT_FOLDER,
  options: UploadOptions = {}
): Promise<S3UploadResult> => {
  if (!file) {
    throw new Error('No file provided for upload');
  }

  if (file.size > 5 * 1024 * 1024) {
    throw new Error('File size should be less than or equal to 5MB');
  }

  const client = ensureClient();
  const timestamp = Date.now();
  const sanitizedFolder = sanitizeFolder(folderName);
  const imageName = buildImageName(file, options, timestamp);
  const key = buildKey(imageName, sanitizedFolder);
  const metadata = normalizeMetadata(options.metadata);

  const fileBuffer = await file.arrayBuffer();

  const command = new PutObjectCommand({
    Bucket: S3_BUCKET,
    Key: key,
    Body: new Uint8Array(fileBuffer),
    ContentType: file.type || 'application/octet-stream',
    Metadata: metadata,
  });

  await client.send(command);

  return {
    imageName,
    fullUrl: buildPublicUrl(key),
    key,
    originalName: file.name,
    size: file.size,
    timestamp,
    metadata,
  };
};

export const uploadToS3 = async (
  file: File,
  folderName = DEFAULT_FOLDER,
  options: UploadOptions = {}
) => {
  const result = await uploadToS3WithImageName(file, folderName, options);
  return result.fullUrl;
};

export const deleteFromS3 = async (fileKey: string) => {
  if (!fileKey) {
    throw new Error('No file key provided for deletion');
  }

  const client = ensureClient();

  const command = new DeleteObjectCommand({
    Bucket: S3_BUCKET,
    Key: trimLeadingSlash(fileKey),
  });

  await client.send(command);
  return true;
};

export const deleteFromS3ByImageName = async (
  imageName: string,
  folderName = DEFAULT_FOLDER
) => {
  if (!imageName) {
    throw new Error('No image name provided for deletion');
  }

  const sanitizedFolder = sanitizeFolder(folderName);
  const key = buildKey(imageName, sanitizedFolder);
  return deleteFromS3(key);
};

const isExternalUrl = (value: string) => /^https?:\/\//i.test(value);
const isDataLikeUrl = (value: string) =>
  value.startsWith('data:') || value.startsWith('blob:');

const maybeRewriteExternalS3UrlToCdn = (url: string) => {
  const base = CDN_BASE_URL.trim();
  if (!base) {
    return url;
  }

  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    const isAws = host.endsWith('amazonaws.com');
    const looksLikeS3Subdomain = host.includes('.s3.');
    const looksLikeS3Host =
      host === 's3.amazonaws.com' || host.startsWith('s3.');

    if (!isAws || (!looksLikeS3Subdomain && !looksLikeS3Host)) {
      return url;
    }

    const rawPath = trimLeadingSlash(parsed.pathname);
    if (!rawPath) {
      return url;
    }

    // For path-style S3 URLs, the first path segment is the bucket name.
    const key = looksLikeS3Host
      ? rawPath.split('/').slice(1).join('/')
      : rawPath;
    return key ? buildPublicUrl(key) : url;
  } catch {
    return url;
  }
};

export const resolveStorageUrl = (
  value: string,
  folderName = DEFAULT_FOLDER
): string => {
  if (!value) {
    return '';
  }

  const trimmed = value.trim();

  if (trimmed === '') {
    return '';
  }

  if (isDataLikeUrl(trimmed)) {
    return trimmed;
  }

  if (isExternalUrl(trimmed)) {
    return maybeRewriteExternalS3UrlToCdn(trimmed);
  }

  if (trimmed.startsWith('s3://')) {
    const withoutProtocol = trimmed.replace(/^s3:\/\//i, '');
    const firstSlashIndex = withoutProtocol.indexOf('/');
    const keyPart =
      firstSlashIndex >= 0
        ? withoutProtocol.slice(firstSlashIndex + 1)
        : withoutProtocol;
    return keyPart ? buildPublicUrl(keyPart) : '';
  }

  const sanitizedFolder = sanitizeFolder(folderName);
  const key = buildKey(trimmed, sanitizedFolder);
  return buildPublicUrl(key);
};

export const getImageUrlFromName = (
  imageName: string,
  folderName = DEFAULT_FOLDER
): string => {
  if (!imageName) {
    return '';
  }
  return resolveStorageUrl(imageName, folderName);
};

export const getImageUrlsFromNames = (
  imageNames: string[] = [],
  folderName = DEFAULT_FOLDER
): string[] => {
  if (!Array.isArray(imageNames)) {
    return [];
  }

  return imageNames
    .filter((name): name is string => Boolean(name && name.trim()))
    .map((name) => getImageUrlFromName(name.trim(), folderName));
};

export const getImageUrlWithFallback = (
  imageName?: string,
  folderName = DEFAULT_FOLDER
): string => {
  const resolved = imageName ? getImageUrlFromName(imageName, folderName) : '';
  return resolved || FALLBACK_IMAGE_URL;
};

export const debugS3Configuration = () => {
  console.groupCollapsed('S3 configuration debug');
  console.table({
    bucket: S3_BUCKET ?? 'unset',
    region: REGION ?? 'unset',
    cdnBaseUrl: CDN_BASE_URL || 'unset',
    hasClient: Boolean(s3Client),
    defaultFolder: DEFAULT_FOLDER,
  });

  if (!hasValidS3Config) {
    console.warn(
      'S3 configuration appears incomplete. Uploads and deletions will fail until all variables are provided.'
    );
  }

  const sampleKey = buildKey('debug-image.jpg', REVIEW_IMAGES_FOLDER);
  console.log('Sample public URL:', buildPublicUrl(sampleKey));
  console.groupEnd();
};

export const uploadProfilePictureToS3 = async (file: File, userId: string) => {
  if (!userId) {
    throw new Error('User ID is required to upload a profile picture');
  }

  const prefix = `user-${userId}`;
  const result = await uploadToS3WithImageName(file, PROFILE_IMAGES_FOLDER, {
    prefix,
  });

  return {
    ...result,
    userId,
  };
};

export const deleteProfilePictureFromS3 = async (imageName: string) => {
  if (!imageName) {
    return false;
  }
  await deleteFromS3ByImageName(imageName, PROFILE_IMAGES_FOLDER);
  return true;
};

export const getProfilePictureUrl = (imageName: string) => {
  return imageName
    ? getImageUrlFromName(imageName, PROFILE_IMAGES_FOLDER)
    : FALLBACK_IMAGE_URL;
};
